// Gamification points & achievement catalogue (spec §11.5).
//
// All UI surfaces that read these values MUST gate themselves through
// useMotivationalDriverVisibility() — extrinsic learners get the full
// XP/badge stack, balanced learners see streak + simple progress, and
// intrinsic learners see the quiet mastery view (nothing from this
// catalogue rendered at all).
//
// Point values mirror architecture v1.8 §11.5.5.

export type GamificationAction =
  // Onboarding (one-time)
  | 'diagnostic_started'
  | 'diagnostic_completed'
  | 'first_upload'
  | 'first_tutor_query'
  // Learning activities (repeatable)
  | 'node_completed'
  | 'checkpoint_passed'
  | 'checkpoint_perfect'
  | 'content_uploaded'
  | 'tutor_query'
  | 'tutor_session_completed'
  | 'recalibration_completed'
  | 'path_completed'
  // Tutor mode bonuses (added on top of tutor_query)
  | 'tutor_mode_explain'
  | 'tutor_mode_go_deeper'
  | 'tutor_mode_test_me'
  | 'tutor_mode_connect'
  | 'tutor_mode_simplify'
  // Streaks
  | 'daily_login'
  | 'streak_day_added'
  | 'week_streak_bonus'
  | 'month_streak_bonus'
  // Teams
  | 'cohort_challenge_joined'
  | 'cohort_challenge_won'
  | 'peer_help_given';

export const POINTS_FOR: Record<GamificationAction, number> = {
  diagnostic_started: 25,
  diagnostic_completed: 100,
  first_upload: 100,
  first_tutor_query: 50,

  node_completed: 25,
  checkpoint_passed: 50,
  checkpoint_perfect: 100,
  content_uploaded: 30,
  tutor_query: 5,
  tutor_session_completed: 25,
  recalibration_completed: 50,
  path_completed: 200,

  tutor_mode_explain: 5,
  tutor_mode_go_deeper: 10,
  tutor_mode_test_me: 15,
  tutor_mode_connect: 10,
  tutor_mode_simplify: 5,

  daily_login: 10,
  streak_day_added: 20,
  week_streak_bonus: 100,
  month_streak_bonus: 500,

  cohort_challenge_joined: 50,
  cohort_challenge_won: 150,
  peer_help_given: 25,
};

export interface AchievementDefinition {
  id: string;
  title: string;
  description: string;
  /** Documented criterion; matched in hasMetAchievementCriterion(). */
  criterion: string;
  xpReward: number;
  /** Brand-aligned tone: 'gold' (extrinsic celebration) | 'indigo' (mastery). */
  tone: 'gold' | 'indigo';
  /** True when the achievement depends on a feature suite that hasn't
   *  shipped yet (Voice §11.3, Knowledge Graph §11.4). UI hides these
   *  from the locked-gallery view until the suite goes live. */
  comingSoon?: boolean;
}

export const ACHIEVEMENTS_CATALOG: AchievementDefinition[] = [
  {
    id: 'first_print',
    title: 'First Print',
    description: 'Completed your CogniPrint diagnostic.',
    criterion: 'diagnosticCompleted',
    xpReward: 0, // Diagnostic completion already awards 100 XP via the action.
    tone: 'gold',
  },
  {
    id: 'deep_diver',
    title: 'Deep Diver',
    description: 'Used Go Deeper mode 10 times.',
    criterion: 'goDeeperModeCount >= 10',
    xpReward: 50,
    tone: 'indigo',
  },
  {
    id: 'test_pilot',
    title: 'Test Pilot',
    description: 'Passed 10 checkpoints.',
    criterion: 'checkpointsPassedCount >= 10',
    xpReward: 100,
    tone: 'gold',
  },
  {
    id: 'polyglot',
    title: 'Polyglot',
    description: 'Uploaded content from three or more subjects.',
    criterion: 'subjectsUploaded.length >= 3',
    xpReward: 75,
    tone: 'indigo',
  },
  {
    id: 'week_warrior',
    title: 'Week Warrior',
    description: 'Studied for 7 consecutive days.',
    criterion: 'currentStreakDays >= 7',
    xpReward: 100,
    tone: 'gold',
  },
  {
    id: 'month_master',
    title: 'Month Master',
    description: 'Studied for 30 consecutive days.',
    criterion: 'currentStreakDays >= 30',
    xpReward: 500,
    tone: 'gold',
  },
  {
    id: 'perfect_run',
    title: 'Perfect Run',
    description: 'Aced five checkpoints in a row.',
    criterion: 'consecutivePerfectCheckpoints >= 5',
    xpReward: 100,
    tone: 'gold',
  },
  {
    id: 'path_pioneer',
    title: 'Path Pioneer',
    description: 'Completed an entire learning path.',
    criterion: 'pathsCompleted >= 1',
    xpReward: 200,
    tone: 'indigo',
  },
  {
    id: 'self_aware',
    title: 'Self-Aware',
    description: 'Completed your first full recalibration.',
    criterion: 'fullRecalibrationsCompleted >= 1',
    xpReward: 50,
    tone: 'indigo',
  },
  // ─── Coming Soon — unlock when the feature suite ships. ───
  {
    id: 'voice_learner',
    title: 'Voice Learner',
    description: 'Complete 10 voice tutor sessions.',
    criterion: 'voiceTutorSessionsCount >= 10',
    xpReward: 75,
    tone: 'gold',
    comingSoon: true,
  },
  {
    id: 'knowledge_mapper',
    title: 'Knowledge Mapper',
    description: 'Explore 50 nodes on the knowledge graph.',
    criterion: 'knowledgeGraphNodesExplored >= 50',
    xpReward: 75,
    tone: 'indigo',
    comingSoon: true,
  },
  {
    id: 'contradiction_finder',
    title: 'Contradiction Finder',
    description: 'Discover a cross-document contradiction.',
    criterion: 'contradictionsFound >= 1',
    xpReward: 50,
    tone: 'indigo',
    comingSoon: true,
  },
];

export function lookupAchievement(id: string): AchievementDefinition | null {
  return ACHIEVEMENTS_CATALOG.find((a) => a.id === id) ?? null;
}
